import React, { useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import {
  Gem,
  Award,
  Shield,
  Check,
  Users,
  Globe,
  TrendingUp,
  Heart,
} from "lucide-react";
import { submitSponsorshipInquiry } from "../services/api";

const TIERS = [
  {
    id: "platinum",
    label: "Platinum Sponsor",
    Icon: Gem,
    color: "#5f6f78",
    ink: "#ffffff",
    accent: "#5f6f78",
    highlight: true,
    price: 10000,
    tagline: "Premier partner across the full 2026 season.",
    benefits: [
      "Premium logo placement at all six 2026 events",
      "First-position live-stream lower third, pre-roll, and end-card",
      "Minimum 12 dedicated social posts plus one takeover day",
      "Premium booth at DTFEST and New Year's Eve",
      "24 VIP passes across the year",
      "Co-branded promo video produced by 3E",
    ],
  },
  {
    id: "gold",
    label: "Gold Sponsor",
    Icon: Award,
    color: "#e6a817",
    ink: "#17120f",
    accent: "#8a6100",
    highlight: false,
    price: 7000,
    tagline: "Prominent multi-event visibility across 2026.",
    benefits: [
      "Featured logo on selected promo materials for major events",
      "Standard booth at DTFEST and New Year's Eve",
      "Group-rotation live broadcast logo card and end-card thanks",
      "Minimum two emcee mentions per event",
      "At least 6 social/email sponsor spotlights",
      "16 passes across events",
    ],
  },
  {
    id: "silver",
    label: "Silver Sponsor",
    Icon: Shield,
    color: "#546e7a",
    ink: "#ffffff",
    accent: "#546e7a",
    highlight: false,
    price: 5000,
    tagline: "Accessible annual partnership with solid presence.",
    benefits: [
      "Sponsor table or small booth at DTFEST or NYE",
      "Logo on shared banner/backdrop and program recognition",
      "At least 3 multi-sponsor social posts",
      "Website sponsor page listing for the full season",
      "8 passes across sponsor-selected events",
      "Group thank-you mention from stage and on signage",
    ],
  },
];

const WHY_STATS = [
  { Icon: Users, value: "15,000+", label: "Annual In-Person Attendees" },
  { Icon: TrendingUp, value: "500,000+", label: "Annual Digital Reach" },
  { Icon: Globe, value: "300,000+", label: "Live-Stream Viewers" },
  { Icon: Heart, value: "20%", label: "Revenue Donated to Community" },
];

const EVENTS_COVERED =
  "College Counseling, FIFA E-Sports, Dance Competition, Storytelling Award, DTFEST, and NYE Celebration";

const ADD_ONS = [
  "Main Stage",
  "VIP Lounge",
  "Food Court",
  "Scholarship Award",
  "E-Sports Bracket",
  "Storyteller Award",
  "Kite Festival",
  "NYE Countdown",
  "Live-Stream Wipe",
];

const formatPrice = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const initialForm = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  website: "",
  tier: "",
  message: "",
};

/**
 * Persist the inquiry first, then attempt the optional browser-side admin
 * notification. Only persistence failure should make the form retryable.
 */
export async function persistSponsorshipSubmission(
  saveInquiry,
  sendAdminNotification,
  reportNotificationFailure = (error) =>
    console.error("Sponsorship admin notification failed:", error)
) {
  await saveInquiry();
  try {
    await sendAdminNotification();
  } catch (error) {
    reportNotificationFailure(error);
  }
}

const Sponsorships = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const selectedTier = TIERS.find((tier) => tier.id === form.tier);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectTier = (tierId) => {
    setForm((prev) => ({ ...prev, tier: tierId }));
    document
      .getElementById("sponsorship-form")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tier) {
      setErrorMsg("Please select a sponsorship tier.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");

    try {
      await persistSponsorshipSubmission(
        () => submitSponsorshipInquiry({
          ...form,
          tier_price: selectedTier?.price || "",
        }),
        () => emailjs.send(
          "service_tl9o3ku",
          "template_6d0djpj",
          {
            from_name: form.contact_name,
            from_email: form.email,
            phone: form.phone || "Not provided",
            subject: `New ${
              TIERS.find((t) => t.id === form.tier)?.label
            } Sponsorship Inquiry — ${form.company_name}`,
            message: `Company: ${form.company_name}\nWebsite: ${
              form.website || "N/A"
            }\nTier: ${selectedTier?.label || form.tier}\nPackage Price: ${
              selectedTier ? formatPrice(selectedTier.price) : "N/A"
            }\nEvents Covered: ${EVENTS_COVERED}\n\nMessage:\n${
              form.message || "No message provided."
            }`,
            to_name: "3E Entertainment",
            to_email: "info@3etainment.com",
          },
          "1ksbR2l12BQUIo516"
        )
      );

      setStatus("success");
      setForm(initialForm);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="sp-page"
    >
      {/* Hero */}
      <section className="sp-hero">
        <div className="sp-hero-overlay" />
        <div className="sp-hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            Invest in Culture.
            <br />
            <span className="sp-hero-accent">Amplify Your Brand.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Partner with 3E Entertainment to reach a passionate, loyal Nepali
            diaspora audience across six 2026 programs, from youth education
            to DTFEST and New Year's Eve.
          </motion.p>
          <motion.a
            href="#sponsorship-form"
            className="sp-hero-btn"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
          >
            Apply Now
          </motion.a>
        </div>
      </section>

      {/* Tiers */}
      <section className="sp-tiers-section">
        <div className="sp-container">
          <div className="sp-section-header">
            <h2>Choose Your Level of Impact</h2>
            <p>
              Every tier is designed to deliver meaningful exposure and
              authentic community connection. Select a tier to pre-fill your
              application; payment is requested only after admin approval.
            </p>
          </div>

          <div className="sp-tiers-grid">
            {TIERS.map((tier, i) => {
              const TierIcon = tier.Icon;
              return (
                <motion.div
                  key={tier.id}
                  className={`sp-tier-card${
                    tier.highlight ? " sp-tier-card--featured" : ""
                  }`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  style={{ "--tier-color": tier.color, "--tier-ink": tier.ink, "--tier-accent": tier.accent }}
                >
                  {tier.highlight && (
                    <div className="sp-tier-tag">Most Impactful</div>
                  )}
                  <div className="sp-tier-icon-wrap">
                    <TierIcon size={26} strokeWidth={1.8} />
                  </div>
                  <h3 className="sp-tier-name">{tier.label}</h3>
                  <div className="sp-tier-price">{formatPrice(tier.price)}</div>
                  <p className="sp-tier-tagline">{tier.tagline}</p>
                  <div className="sp-tier-events">Covers all six 2026 events</div>
                  <ul className="sp-tier-benefits">
                    {tier.benefits.map((b) => (
                      <li key={b}>
                        <Check size={14} strokeWidth={2.5} className="sp-check" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    className="sp-tier-btn"
                    onClick={() => handleSelectTier(tier.id)}
                  >
                    Apply for {tier.label}
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="sp-addons-section">
        <div className="sp-container">
          <div className="sp-addons-panel">
            <div>
              <h2>Build Your Moment</h2>
              <p>
                Add a focused activation on top of your tier, or ask us about a
                custom package. Add-ons typically range from $1,000 to $2,500.
              </p>
            </div>
            <div className="sp-addons-list">
              {ADD_ONS.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Sponsor */}
      <section className="sp-why-section">
        <div className="sp-container">
          <div className="sp-why-grid">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2>Connect With a Community That Cares</h2>
              <p>
                3E Entertainment has spent over two decades building deep trust
                with the Nepali diaspora. Our audiences do not just attend
                events — they celebrate their identity. Your brand becomes part
                of that story.
              </p>
            </motion.div>
            <div className="sp-why-stats">
              {WHY_STATS.map((stat, i) => {
                const StatIcon = stat.Icon;
                return (
                  <motion.div
                    key={stat.label}
                    className="sp-stat"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <StatIcon size={22} strokeWidth={1.6} className="sp-stat-icon" />
                    <div className="sp-stat-value">{stat.value}</div>
                    <div className="sp-stat-label">{stat.label}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="sp-form-section" id="sponsorship-form">
        <div className="sp-container sp-container--narrow">
          <div className="sp-section-header">
            <h2>Submit Your Sponsorship Inquiry</h2>
            <p>
              Fill out the form below. Our team reviews every application and
              responds within 3 to 5 business days.
            </p>
          </div>

          {status === "success" ? (
            <motion.div
              className="sp-success-box"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="sp-success-icon-wrap">
                <Check size={36} strokeWidth={2} />
              </div>
              <h3>Application Received</h3>
              <p>
                Thank you for your interest in sponsoring 3E Entertainment. Our
                team will review your inquiry and email the Stripe payment link
                only if your sponsorship is approved.
              </p>
            </motion.div>
          ) : (
            <motion.form
              className="sp-form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="sp-form-group sp-form-full">
                <label>
                  Sponsorship Tier <span className="sp-req">*</span>
                </label>
                <div className="sp-tier-pills">
                  {TIERS.map((tier) => {
                    const PillIcon = tier.Icon;
                    return (
                      <button
                        key={tier.id}
                        type="button"
                        className={`sp-tier-pill${
                          form.tier === tier.id ? " active" : ""
                        }`}
                        style={{ "--tc": tier.color }}
                        onClick={() => setForm((p) => ({ ...p, tier: tier.id }))}
                      >
                        <PillIcon size={14} strokeWidth={2} />
                        <span>{tier.label}</span>
                        <strong>{formatPrice(tier.price)}</strong>
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedTier && (
                <div className="sp-selected-package sp-form-full">
                  <div>
                    <span>Selected package</span>
                    <strong>{selectedTier.label}</strong>
                    <p>{selectedTier.tagline}</p>
                  </div>
                  <div className="sp-selected-price">
                    {formatPrice(selectedTier.price)}
                  </div>
                </div>
              )}

              <div className="sp-form-row">
                <div className="sp-form-group">
                  <label>
                    Company or Organization <span className="sp-req">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    className="sp-input"
                    value={form.company_name}
                    onChange={handleChange}
                    placeholder="Your organization name"
                    required
                  />
                </div>
                <div className="sp-form-group">
                  <label>
                    Contact Name <span className="sp-req">*</span>
                  </label>
                  <input
                    type="text"
                    name="contact_name"
                    className="sp-input"
                    value={form.contact_name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                  />
                </div>
              </div>

              <div className="sp-form-row">
                <div className="sp-form-group">
                  <label>
                    Email Address <span className="sp-req">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    className="sp-input"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@yourorganization.com"
                    required
                  />
                </div>
                <div className="sp-form-group">
                  <label>
                    Phone <span className="sp-opt">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="sp-input"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Best number to reach you"
                  />
                </div>
              </div>

              <div className="sp-form-group sp-form-full">
                <label>
                  Website <span className="sp-opt">(optional)</span>
                </label>
                <input
                  type="text"
                  inputMode="url"
                  name="website"
                  className="sp-input"
                  value={form.website}
                  onChange={handleChange}
                  placeholder="https://yourorganization.com"
                />
              </div>

              <div className="sp-form-group sp-form-full">
                <label>
                  Tell us about your goals{" "}
                  <span className="sp-opt">(optional)</span>
                </label>
                <textarea
                  name="message"
                  className="sp-input sp-textarea"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="What do you hope to achieve through this sponsorship? Any specific events or audiences you have in mind?"
                />
              </div>

              {errorMsg && (
                <div className="sp-error-msg">{errorMsg}</div>
              )}

              <button
                type="submit"
                className="sp-submit-btn"
                disabled={status === "loading"}
              >
                {status === "loading" ? "Submitting..." : "Submit Application"}
              </button>
              <p className="sp-form-note">
                By submitting, you agree to be contacted by 3E Entertainment
                regarding your inquiry. No payment is collected at this stage,
                and the Stripe payment link is sent only after approval.
              </p>
            </motion.form>
          )}
        </div>
      </section>

      <style>{`
        .sp-page { overflow-x: hidden; }

        /* Hero */
        .sp-hero {
          position: relative;
          min-height: 72vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("/images/R5BI6877.webp") center / cover no-repeat;
        }
        .sp-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(10,8,6,0.92) 0%, rgba(19,15,13,0.78) 100%);
        }
        .sp-hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 780px;
          padding: 8rem 2rem 4rem;
        }
        .sp-hero h1 {
          font-size: clamp(2.6rem, 6vw, 4.2rem);
          font-weight: 900;
          color: #f8f2ea;
          line-height: 1.05;
          margin: 0 0 1.25rem;
          letter-spacing: -0.02em;
        }
        .sp-hero-accent { color: #C72631; }
        .sp-hero p {
          font-size: 1.1rem;
          color: rgba(248,242,234,0.72);
          line-height: 1.75;
          margin: 0 0 2rem;
        }
        .sp-hero-btn {
          display: inline-block;
          background: #C72631;
          color: #fff;
          padding: 1rem 2.5rem;
          border-radius: 999px;
          font-weight: 700;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          text-decoration: none;
          transition: background 0.3s, transform 0.3s;
          box-shadow: 0 16px 40px rgba(199,38,49,0.3);
        }
        .sp-hero-btn:hover { background: #a91f29; transform: translateY(-2px); }

        /* Shared */
        .sp-container { max-width: 1200px; margin: 0 auto; padding: 0 2rem; }
        .sp-container--narrow { max-width: 820px; }
        .sp-section-header { text-align: center; max-width: 680px; margin: 0 auto 3.5rem; }
        .sp-section-header h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1rem;
          line-height: 1.1;
        }
        .sp-section-header p { font-size: 1rem; color: #666; line-height: 1.75; margin: 0; }

        /* Tiers */
        .sp-tiers-section { padding: 7rem 0; background: #f8f8f8; }
        .sp-tiers-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
          align-items: start;
        }
        .sp-tier-card {
          background: #fff;
          border: 1.5px solid #ebebeb;
          border-radius: 20px;
          padding: 2.25rem 1.75rem;
          position: relative;
          transition: transform 0.35s cubic-bezier(0.32,0.72,0,1), box-shadow 0.35s, border-color 0.35s;
        }
        .sp-tier-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 24px 56px rgba(0,0,0,0.1);
          border-color: var(--tier-color);
        }
        .sp-tier-card--featured {
          border-color: var(--tier-color);
          box-shadow: 0 8px 32px rgba(0,0,0,0.07);
        }
        .sp-tier-tag {
          position: absolute;
          top: -1px;
          right: 1.5rem;
          background: #C72631;
          color: #fff;
          font-size: 0.75rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          padding: 0.3rem 0.85rem;
          border-radius: 0 0 8px 8px;
        }
        .sp-tier-icon-wrap {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(0,0,0,0.05);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--tier-accent, var(--tier-color));
          margin-bottom: 1rem;
        }
        .sp-tier-name { font-size: 1.15rem; font-weight: 800; color: #0a0a0a; margin: 0 0 0.35rem; }
        .sp-tier-price {
          font-size: 2rem;
          font-weight: 900;
          color: #0a0a0a;
          line-height: 1;
          margin-bottom: 0.65rem;
        }
        .sp-tier-tagline { font-size: 0.84rem; color: var(--text-muted-light, #6e6e6e); line-height: 1.5; margin: 0 0 1.4rem; }
        .sp-tier-events {
          background: rgba(199,38,49,0.07);
          color: #7c1a22;
          border: 1px solid rgba(199,38,49,0.14);
          border-radius: 10px;
          padding: 0.6rem 0.75rem;
          font-size: 0.78rem;
          font-weight: 700;
          margin-bottom: 1.25rem;
        }
        .sp-tier-benefits {
          list-style: none;
          padding: 0;
          margin: 0 0 1.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }
        .sp-tier-benefits li {
          font-size: 0.84rem;
          color: #444;
          line-height: 1.5;
          display: flex;
          gap: 0.5rem;
          align-items: flex-start;
        }
        .sp-check { color: var(--tier-accent, var(--tier-color)); flex-shrink: 0; margin-top: 2px; }
        .sp-tier-btn {
          width: 100%;
          min-height: 44px;
          padding: 0.85rem 1.25rem;
          background: var(--tier-color);
          color: var(--tier-ink, #fff);
          border: none;
          border-radius: 12px;
          font-size: 0.84rem;
          font-weight: 700;
          cursor: pointer;
          transition: opacity 0.25s, transform 0.25s;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .sp-tier-btn:hover { opacity: 0.85; transform: translateY(-1px); }

        /* Add-ons */
        .sp-addons-section { padding: 0 0 7rem; background: #f8f8f8; }
        .sp-addons-panel {
          display: grid;
          grid-template-columns: 0.9fr 1.1fr;
          gap: 2.5rem;
          align-items: center;
          background: #111;
          color: #fff;
          border-radius: 20px;
          padding: 2.25rem;
        }
        .sp-addons-panel h2 { color: #fff; margin: 0 0 0.8rem; font-size: clamp(1.65rem, 3vw, 2.3rem); line-height: 1.05; }
        .sp-addons-panel p { margin: 0; color: rgba(255,255,255,0.68); line-height: 1.65; }
        .sp-addons-list { display: flex; flex-wrap: wrap; gap: 0.65rem; }
        .sp-addons-list span {
          color: rgba(255,255,255,0.88);
          border: 1px solid rgba(255,255,255,0.18);
          background: rgba(255,255,255,0.06);
          border-radius: 999px;
          padding: 0.65rem 0.9rem;
          font-size: 0.84rem;
          font-weight: 700;
        }

        /* Why Sponsor */
        .sp-why-section { padding: 7rem 0; background: #fff; }
        .sp-why-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 5rem; align-items: center; }
        .sp-why-grid h2 {
          font-size: clamp(1.8rem, 3.5vw, 2.6rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1.25rem;
          line-height: 1.1;
        }
        .sp-why-grid p { font-size: 1rem; color: #666; line-height: 1.8; }
        .sp-why-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.25rem; }
        .sp-stat { min-width: 0; }
        .sp-stat {
          background: #f8f8f8;
          border-radius: 16px;
          padding: 1.75rem;
        }
        .sp-stat-icon { color: #C72631; margin-bottom: 0.75rem; }
        .sp-stat-value { font-size: 2rem; font-weight: 900; color: #0a0a0a; line-height: 1; margin-bottom: 0.35rem; }
        .sp-stat-label { font-size: 0.84rem; color: #666; line-height: 1.4; }

        /* Form */
        .sp-form-section { padding: 7rem 0; background: #f8f8f8; }
        .sp-form {
          background: #fff;
          border-radius: 24px;
          padding: 2.75rem;
          border: 1.5px solid #ebebeb;
          box-shadow: 0 4px 24px rgba(0,0,0,0.05);
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.25rem;
        }
        .sp-form-full { grid-column: 1 / -1; }
        .sp-form-row { grid-column: 1 / -1; display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
        .sp-form-group { display: flex; flex-direction: column; gap: 0.45rem; }
        .sp-form-group label { font-size: 0.85rem; font-weight: 600; color: #333; }
        .sp-req { color: #C72631; }
        .sp-opt { color: var(--text-muted-light, #6e6e6e); font-weight: 400; font-size: 0.8rem; }
        .sp-input {
          padding: 0.85rem 1rem;
          border: 1.5px solid #e5e5e5;
          border-radius: 10px;
          font-size: 0.95rem;
          color: #0a0a0a;
          outline: none;
          transition: border-color 0.25s, background 0.25s;
          font-family: inherit;
          background: #fafafa;
          width: 100%;
        }
        .sp-input:focus { border-color: #C72631; background: #fff; }
        .sp-textarea { resize: vertical; min-height: 110px; }
        .sp-tier-pills { display: flex; flex-wrap: wrap; gap: 0.6rem; }
        .sp-tier-pill {
          display: inline-flex;
          align-items: center;
          min-height: 44px;
          gap: 0.45rem;
          padding: 0.6rem 0.95rem;
          border: 1.5px solid #e5e5e5;
          border-radius: 999px;
          background: #fafafa;
          font-size: 0.84rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.25s;
          color: #555;
          font-family: inherit;
        }
        .sp-tier-pill strong { font-size: 0.75rem; }
        .sp-tier-pill:hover { border-color: var(--tc); color: var(--tc); background: rgba(0,0,0,0.03); }
        .sp-tier-pill.active { background: var(--tc); border-color: var(--tc); color: #fff; }
        .sp-selected-package {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          background: #fafafa;
          border: 1.5px solid #e8e8e8;
          border-radius: 14px;
          padding: 1.1rem 1.25rem;
        }
        .sp-selected-package span {
          display: block;
          color: var(--text-muted-light, #6e6e6e);
          font-size: 0.75rem;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 0.25rem;
        }
        .sp-selected-package strong { color: #0a0a0a; font-size: 1rem; }
        .sp-selected-package p { margin: 0.25rem 0 0; color: #666; font-size: 0.84rem; line-height: 1.45; }
        .sp-selected-price { color: #C72631; font-size: 1.45rem; font-weight: 900; white-space: nowrap; }
        .sp-error-msg {
          grid-column: 1 / -1;
          background: rgba(199,38,49,0.07);
          border: 1px solid rgba(199,38,49,0.22);
          color: #C72631;
          padding: 0.75rem 1rem;
          border-radius: 10px;
          font-size: 0.9rem;
        }
        .sp-submit-btn {
          grid-column: 1 / -1;
          background: #C72631;
          color: #fff;
          border: none;
          padding: 1.1rem 2rem;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          transition: background 0.3s, transform 0.3s;
          box-shadow: 0 8px 24px rgba(199,38,49,0.22);
          font-family: inherit;
        }
        .sp-submit-btn:hover:not(:disabled) { background: #a91f29; transform: translateY(-2px); }
        .sp-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .sp-form-note {
          grid-column: 1 / -1;
          font-size: 0.78rem;
          color: var(--text-muted-light, #6e6e6e);
          text-align: center;
          margin: 0;
          line-height: 1.5;
        }
        .sp-success-box {
          background: #fff;
          border-radius: 24px;
          padding: 4rem 2rem;
          text-align: center;
          border: 1.5px solid #ebebeb;
          box-shadow: 0 4px 24px rgba(0,0,0,0.05);
        }
        .sp-success-icon-wrap {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: rgba(76,175,80,0.1);
          border: 1.5px solid rgba(76,175,80,0.25);
          color: #4caf50;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }
        .sp-success-box h3 { font-size: 1.8rem; font-weight: 900; color: #0a0a0a; margin: 0 0 1rem; }
        .sp-success-box p { font-size: 1rem; color: #666; line-height: 1.75; max-width: 520px; margin: 0 auto; }

        /* Responsive */
        @media (max-width: 1100px) { .sp-tiers-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 900px) {
          .sp-why-grid { grid-template-columns: 1fr; gap: 3rem; }
          .sp-addons-panel { grid-template-columns: 1fr; }
          .sp-form { padding: 2rem 1.5rem; }
        }
        @media (max-width: 640px) {
          .sp-tiers-grid { grid-template-columns: 1fr; max-width: 420px; margin: 0 auto; }
          .sp-form { grid-template-columns: 1fr; }
          .sp-form-full, .sp-form-row, .sp-submit-btn, .sp-error-msg, .sp-form-note { grid-column: 1 / -1; }
          .sp-form-row { grid-template-columns: 1fr; }
          .sp-selected-package { align-items: flex-start; flex-direction: column; }
          .sp-tiers-section, .sp-why-section, .sp-form-section { padding: 4.5rem 0; }
          .sp-addons-section { padding: 0 0 4.5rem; }
          .sp-container { padding: 0 1.25rem; }
          .sp-hero-content { padding: 7rem 1.25rem 3rem; }
        }
      `}</style>
    </motion.div>
  );
};

export default Sponsorships;
